import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import React from 'react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { SearchInput, Select, Button, BottomSheet, JalaliDatePicker } from '@parsina/framework';
import { CalendarDays, SlidersHorizontal, FilterX } from 'lucide-react';
import { TYPE_OPTIONS } from '../../screens/Accounting/Accounting.shared';

// The mobile accounting filter bar (DESIGN-CONCEPT-MOBILE v2 §6). The two most
// frequent controls stay inline and one-tap — the free-text search and the
// settlement segment («چقدر تسویه نشده؟» is a core reason to open this screen).
// Everything else (date range, service type, clinic) is a BottomSheet, the
// mobile default overlay for filters, reached from two labelled buttons so the
// bar never crowds a 320px width.

// Short settlement labels — the desktop tabs' full wording («در انتظار تسویه»)
// won't sit three-up on a phone. Same ids the report speaks.
const SETTLE_TABS = (t) => ([
  { id: 'ALL',     label: t('common.state.all') },
  { id: 'PENDING', label: t('common.state.pending') },
  { id: 'SETTLED', label: t('common.settlement.SETTLED') },
]);

const MobileAccountingFilters = ({
  search, onSearch,
  tab, onTab,
  preset, custom, onPreset, onCustom, presets, rangeLabel,
  itemType, onType,
  clinicId, onClinic, clinics = [],
  activeCount = 0, onClear,
}) => {
  const { t, formatDigits } = useTranslation();
  const [rangeOpen, setRangeOpen] = React.useState(false);
  const [moreOpen, setMoreOpen]   = React.useState(false);

  // Custom-range draft — committed only when both ends are set.
  const [draft, setDraft] = React.useState({ from: custom.from || '', to: custom.to || '' });
  React.useEffect(() => { setDraft({ from: custom.from || '', to: custom.to || '' }); }, [custom.from, custom.to]);

  const clinicOptions = clinics.map((c) => ({ value: c.id, label: c.name }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }} data-testid="accounting-toolbar">

      {/* Search — leads the bar, full width. */}
      <div data-testid="accounting-toolbar-search">
        <SearchInput
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={t('accounting.searchPlaceholder')}
          clearLabel={t('common.actions.clearSearch')}
        />
      </div>

      {/* Settlement — the primary inline filter, as a full-width 44px button group
          (a real touch target, §6 — the kit's segmented pill is too short for a
          thumb). */}
      <div
        role="tablist" aria-label={t('accounting.settlementFilter')}
        data-testid="accounting-toolbar-settlement"
        style={{ display: 'flex', gap: '0.375rem' }}
      >
        {SETTLE_TABS(t).map((item) => {
          const active = tab === item.id;
          return (
            <Button
              key={item.id}
              role="tab" aria-selected={active}
              variant={active ? 'default' : 'outline'}
              size="sm"
              className="flex-1 min-h-11 px-2"
              data-testid={`accounting-toolbar-settlement-${item.id}`}
              onClick={() => onTab(item.id)}
            >
              {item.label}
            </Button>
          );
        })}
      </div>

      {/* Range + more-filters — two labelled buttons opening their sheets. */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          variant="outline" size="sm"
          onClick={() => setRangeOpen(true)}
          data-testid="accounting-toolbar-range"
          className="flex-1 min-h-11 justify-start gap-1.5"
        >
          <CalendarDays size={15} aria-hidden />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rangeLabel}</span>
        </Button>

        <Button
          variant="outline" size="sm"
          onClick={() => setMoreOpen(true)}
          data-testid="accounting-toolbar-more"
          className="flex-1 min-h-11 justify-start gap-1.5"
        >
          <SlidersHorizontal size={15} aria-hidden />
          {t('common.actions.filters')}
          {activeCount > 0 && (
            <span style={{
              marginInlineStart: 'auto',
              minWidth: '1.25rem', height: '1.25rem', borderRadius: '0.75rem',
              background: C.navy, color: 'white',
              fontSize: '0.6875rem', fontWeight: 800,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 0.375rem',
            }}>
              {formatDigits(activeCount)}
            </span>
          )}
        </Button>
      </div>

      {activeCount > 0 && onClear && (
        <Button
          variant="ghost" size="sm"
          onClick={onClear}
          data-testid="accounting-toolbar-clear"
          className="self-start min-h-11 gap-1.5 text-muted-foreground"
        >
          <FilterX size={15} aria-hidden />
          {t('accounting.clearAllFilters')}
        </Button>
      )}

      {/* ── Range sheet ── */}
      <BottomSheet open={rangeOpen} onClose={() => setRangeOpen(false)} title={t('common.labels.dateRange')} closeLabel={t('common.actions.close')}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }} data-testid="accounting-range-presets">
          {presets.map((p) => {
            const active = preset === p.id;
            return (
              <Button
                key={p.id}
                variant={active ? 'default' : 'outline'}
                size="sm"
                className="min-h-11"
                onClick={() => { onPreset(p.id); setRangeOpen(false); }}
              >
                {p.label}
              </Button>
            );
          })}
        </div>

        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textMuted, marginBottom: '0.5rem' }}>{t('common.labels.customRange')}</div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div style={{ flex: 1 }}>
              <JalaliDatePicker value={draft.from} onChange={(v) => setDraft((d) => ({ ...d, from: v }))} placeholder={t('common.labels.fromDate')} disableFuture />
            </div>
            <div style={{ flex: 1 }}>
              <JalaliDatePicker value={draft.to} onChange={(v) => setDraft((d) => ({ ...d, to: v }))} placeholder={t('common.labels.toDate')} disableFuture />
            </div>
          </div>
          <Button
            variant="default"
            className="mt-3 w-full min-h-11"
            disabled={!draft.from || !draft.to}
            onClick={() => { onCustom(draft.from, draft.to); setRangeOpen(false); }}
          >
            {t('accounting.applyCustomRange')}
          </Button>
        </div>
      </BottomSheet>

      {/* ── More-filters sheet ── */}
      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title={t('common.actions.filters')} closeLabel={t('common.actions.close')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textMuted, display: 'block', marginBottom: '0.375rem' }}>{t('accounting.serviceType')}</span>
            <Select value={itemType} onChange={(e) => onType(e.target.value)} options={TYPE_OPTIONS(t)} placeholder={t('accounting.allServices')} />
          </label>

          {clinics.length > 0 && (
            <label style={{ display: 'block' }} data-testid="accounting-filter-clinic">
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textMuted, display: 'block', marginBottom: '0.375rem' }}>{t('common.labels.clinic')}</span>
              <Select value={clinicId} onChange={(e) => onClinic(e.target.value)} options={clinicOptions} placeholder={t('accounting.allClinics')} />
            </label>
          )}

          <Button variant="default" className="w-full min-h-11" onClick={() => setMoreOpen(false)}>
            {t('accounting.viewResults')}
          </Button>
          {activeCount > 0 && onClear && (
            <Button variant="ghost" className="w-full min-h-11 gap-1.5 text-muted-foreground" onClick={() => { onClear(); setMoreOpen(false); }}>
              <FilterX size={15} aria-hidden />
              {t('accounting.clearAllFilters')}
            </Button>
          )}
        </div>
      </BottomSheet>
    </div>
  );
};

export default MobileAccountingFilters;
